import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import AdminHeader from '../../../components/admin/common/AdminHeader';
import AdminBreadcrumb from '../../../components/admin/common/AdminBreadcrumb';
import ProductForm from '../../../components/admin/forms/ProductForm';
import { productService } from '../../../services/productService';
import type { FirebaseProduct, ProductFormData } from '../../../types/product';

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [product, setProduct] = useState<FirebaseProduct | null>(null);

  // First, check authentication
  useEffect(() => {
    console.log('Auth state:', { isAuthenticated, user }); // Debug log
    
    // Wait a brief moment to ensure auth state is properly initialized
    const timer = setTimeout(() => {
      setAuthChecked(true);
      if (!isAuthenticated) {
        const currentPath = location.pathname;
        navigate('/auth/login', { 
          state: { from: currentPath },
          replace: true 
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [isAuthenticated, user, navigate, location]);

  // Then, fetch product data once auth is confirmed
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id || !isAuthenticated) return;
      
      try {
        setLoading(true);
        console.log('Fetching product with ID:', id); // Debug log
        const productData = await productService.getProduct(id);
        
        if (productData) {
          console.log('Product data fetched:', productData); // Debug log
          setProduct(productData);
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    if (authChecked && isAuthenticated) {
      fetchProduct();
    }
  }, [id, isAuthenticated, authChecked]);

  const handleSubmit = async (formData: ProductFormData) => {
    if (!id || !product) return;

    try {
      await productService.updateProduct(id, {
        ...product,
        ...formData,
        updatedAt: new Date()
      });

      navigate('/admin/products', {
        state: { message: 'Product updated successfully' }
      });
    } catch (err) {
      setError('Failed to update product');
      console.error('Error updating product:', err);
    }
  };

  if (!authChecked || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will be redirected by the auth effect
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error || 'Product not found'}
        </div>
        <button
          onClick={() => navigate('/admin/products')}
          className="mt-4 text-brand-blue-500 hover:text-brand-blue-600"
        >
          Back to Products
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminHeader
        title="Edit Product"
        onMenuClick={() => {}}
        actions={
          <button
            onClick={() => navigate('/admin/products')}
            className="inline-flex items-center px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Products
          </button>
        }
      />

      <div className="container mx-auto px-4">
        <AdminBreadcrumb
          items={[
            { label: 'Products', href: '/admin/products' },
            { label: product.name }
          ]}
        />

        <div className="mt-6 bg-white rounded-lg shadow-sm">
          <div className="p-6">
            <ProductForm
              initialData={product}
              onSubmit={handleSubmit}
              submitText="Update Product"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProduct;